import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Pagination,
  Switch,
  Button,
  TextField
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import AddIcon from '@mui/icons-material/Add';

const CategoryList = ({ onEdit, onAdd }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 10;

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = () => {
    setLoading(true);
    axios.get('/api/categories')
      .then(res => setCategories(res.data))
      .catch(err => console.error('Error loading categories:', err))
      .finally(() => setLoading(false));
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await axios.delete(`/api/categories/${id}`);
        setCategories(categories.filter(cat => cat.id !== id));
      } catch (error) {
        alert(error.response?.data?.error || 'Failed to delete category');
      }
    }
  };

  const handleStatusToggle = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await axios.patch(`/api/categories/${id}/status`, { status: newStatus });
      setCategories(categories.map(cat => 
        cat.id === id ? { ...cat, status: newStatus } : cat
      ));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCategories = filteredCategories.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);

  return (
    <Box>
      <Box sx={{ 
        mb: 1.5, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: 2
      }}>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 600,
            color: '#424242',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: '0.875rem'
          }}
        >
          <CategoryIcon sx={{ fontSize: 18 }} />
          All Categories ({filteredCategories.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            sx={{
              fontSize: '0.8rem',
              width: '180px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                }
              }
            }}
          />
          <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={onAdd}
          size="small"
          sx={{ 
            bgcolor: '#43a047',
            px: 2,
            py: 0.5,
            fontSize: '0.75rem',
            textTransform: 'none',
            '&:hover': { 
              bgcolor: '#388e3c'
            }
          }}
        >
          Add Category
        </Button>
        </Box>
      </Box>

      {categories.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5' }}>
          <Typography color="text.secondary">
            No categories found. Create your first category above.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ 
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: '#f5f5f5'
                }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Sub-Categories</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75, color: 'text.primary', borderBottom: '2px solid', borderColor: 'divider' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCategories.map((cat) => (
                <TableRow 
                  key={cat.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: 'action.hover'
                    },
                    '& .MuiTableCell-root': {
                      py: 0.75,
                      px: 1.5,
                      fontSize: '0.8rem'
                    }
                  }}
                >
                  <TableCell>{cat.id}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                      {cat.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Switch
                        checked={cat.status === 1}
                        onChange={() => handleStatusToggle(cat.id, cat.status)}
                        size="small"
                        color="success"
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {cat.status === 1 ? 'Active' : 'Inactive'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={cat.sub_category_count || 0}
                      size="small"
                      sx={{ 
                        height: '20px',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        minWidth: 40
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => onEdit(cat)}
                      size="small"
                      sx={{ 
                        color: '#1976d2',
                        '&:hover': { 
                          bgcolor: '#e3f2fd'
                        }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(cat.id, cat.name)}
                      size="small"
                      sx={{ 
                        color: '#d32f2f',
                        ml: 1,
                        '&:hover': { 
                          bgcolor: '#ffebee'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              onChange={(e, page) => setCurrentPage(page)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        </>
      )}
    </Box>
  );
};

export default CategoryList;
